import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { validatePassword, PasswordRequirements } from '../utils/validatePassword';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import LandingDemoReel from '../components/LandingDemoReel';
import InstallAppCard from '../components/InstallAppCard';
import Wordmark from '../components/Wordmark';
import {
  GraduationCap,
  Users,
  Microphone,
  GameController,
  Video,
  Robot,
  Check,
  ArrowRight,
  Globe,
  ChartLineUp,
  Brain,
  BookOpen,
  Certificate,
  X,
  Eye,
  EyeSlash,
  Clock,
  TrendUp,
  Lightbulb,
  Tag,
  Heart,
  MapPin,
  ChatCircleText,
  PaperPlaneTilt,
  Envelope,
  ChartBar,
  CaretRight,
  CaretDown,
  Crown,
  SealCheck,
  Gift,
  CheckCircle
} from '@phosphor-icons/react';

const ALBERTO_PHOTO = "https://customer-assets.emergentagent.com/job_habla-daily/artifacts/jlvogk0e_Foto%20Curriculum%20buena%20recortada%20-%20copia%20%281%29.jpg";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// Auto-applied promo when the teacher toggles `coupon_40_active` from the
// Verwaltung tab. Kept in sync with VALID_COUPONS.MATRIKEL40 on the backend.
const PROMO_CODE = 'MATRIKEL40';
const PROMO_PERCENT = 40;

const TITULOS = [
  { name: 'Profesor General — Universidad de Salamanca', pdf: 'https://customer-assets.emergentagent.com/job_habla-daily/artifacts/bqgi6alz_TITULO%20MAGISTERIO%20ALBERTO.pdf' },
  { name: 'Audicion y Lenguaje — Universidad a Distancia de Madrid', pdf: 'https://customer-assets.emergentagent.com/job_habla-daily/artifacts/gpjxc6l5_Audici%C3%B3n%20y%20Lenguaje.%20Luis%20Alberto%20Cubria%20Merino.pdf' },
  { name: 'Profesor de Inglés — Universidad Católica de Ávila', pdf: 'https://customer-assets.emergentagent.com/job_habla-daily/artifacts/88hhfjmj_MENCI%C3%93N%20INGL%C3%89S.pdf' },
  { name: 'DECA — Universidad CEU San Pablo', pdf: 'https://customer-assets.emergentagent.com/job_habla-daily/artifacts/asv9ie64_DECA.pdf' },
  { name: 'Organizacion Escolar: Funcion Directiva — Universidad Camilo Jose Cela', pdf: 'https://customer-assets.emergentagent.com/job_habla-daily/artifacts/u4csbxxb_Luis%20Alberto%20Cubr%C3%ADa%20Merino%20%28Organizaci%C3%B3n%20escolar%29.pdf' },
];

const ProgressChart = () => (
  <svg viewBox="0 0 500 280" className="w-full h-auto" data-testid="progress-chart">
    <defs>
      <linearGradient id="chartGrad" x1="0%" y1="0%" x2="100%" y2="0%">
        <stop offset="0%" stopColor="#AB1C24" />
        <stop offset="100%" stopColor="#EAB308" />
      </linearGradient>
      <linearGradient id="areaGrad" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#AB1C24" stopOpacity="0.15" />
        <stop offset="100%" stopColor="#AB1C24" stopOpacity="0.01" />
      </linearGradient>
    </defs>
    {/* Grid */}
    {[240, 190, 140, 90, 40].map((y, i) => (
      <line key={i} x1="60" y1={y} x2="470" y2={y} stroke="#E7E5E4" strokeWidth="1" strokeDasharray="4,4" />
    ))}
    {/* Y labels */}
    <text x="40" y="244" textAnchor="end" fill="#78716C" fontSize="12" fontFamily="DM Sans">A2</text>
    <text x="40" y="194" textAnchor="end" fill="#78716C" fontSize="12" fontFamily="DM Sans">A2+</text>
    <text x="40" y="144" textAnchor="end" fill="#78716C" fontSize="12" fontFamily="DM Sans">B1-</text>
    <text x="40" y="94" textAnchor="end" fill="#78716C" fontSize="12" fontFamily="DM Sans">B1</text>
    <text x="40" y="44" textAnchor="end" fill="#78716C" fontSize="12" fontFamily="DM Sans">B1+</text>
    {/* X labels */}
    {['Monat 1', 'Monat 2', 'Monat 3', 'Monat 4', 'Monat 5', 'Monat 6'].map((m, i) => (
      <text key={i} x={90 + i * 75} y={268} textAnchor="middle" fill="#78716C" fontSize="11" fontFamily="DM Sans">{m}</text>
    ))}
    {/* Area */}
    <path d="M90,235 Q165,220 165,200 Q200,175 240,160 Q280,140 315,115 Q350,95 390,75 Q430,58 465,45 L465,240 L90,240 Z" fill="url(#areaGrad)" />
    {/* Line */}
    <path d="M90,235 Q165,220 165,200 Q200,175 240,160 Q280,140 315,115 Q350,95 390,75 Q430,58 465,45" fill="none" stroke="url(#chartGrad)" strokeWidth="3" strokeLinecap="round" />
    {/* Dots */}
    {[[90,235],[165,200],[240,160],[315,115],[390,75],[465,45]].map(([x,y], i) => (
      <g key={i}>
        <circle cx={x} cy={y} r="6" fill="white" stroke="#AB1C24" strokeWidth="2.5" />
        {i === 5 && <circle cx={x} cy={y} r="10" fill="none" stroke="#EAB308" strokeWidth="2" opacity="0.5"><animate attributeName="r" from="10" to="18" dur="1.5s" repeatCount="indefinite"/><animate attributeName="opacity" from="0.5" to="0" dur="1.5s" repeatCount="indefinite"/></circle>}
      </g>
    ))}
    {/* Current level marker */}
    <rect x="420" y="28" rx="6" ry="6" width="55" height="24" fill="#EAB308" />
    <text x="447" y="44" textAnchor="middle" fill="white" fontSize="12" fontWeight="bold" fontFamily="DM Sans">B1!</text>
  </svg>
);

const PdfViewerModal = ({ titulo, onClose }) => {
  const googleViewerUrl = `https://docs.google.com/gview?url=${encodeURIComponent(titulo.pdf)}&embedded=true`;
  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid="pdf-viewer-modal" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-stone-200 bg-[#FAF9F6]">
          <div className="flex items-center gap-3">
            <Certificate size={22} className="text-[#AB1C24]" weight="duotone" />
            <h3 className="font-heading font-semibold text-[#292524]">{titulo.name}</h3>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-stone-200 transition-colors" data-testid="close-pdf-modal">
            <X size={20} className="text-[#78716C]" />
          </button>
        </div>
        <div className="h-[75vh] bg-[#F5F5F4]">
          <iframe
            src={googleViewerUrl}
            title={titulo.name}
            className="w-full h-full border-0"
          />
        </div>
      </div>
    </div>
  );
};

const LandingPage = () => {
  const [viewingTitulo, setViewingTitulo] = useState(null);
  const [showLegal, setShowLegal] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [couponApplied, setCouponApplied] = useState(false);
  const [couponFree, setCouponFree] = useState(false);
  const [couponPercent, setCouponPercent] = useState(0);
  const [couponFinalAmount, setCouponFinalAmount] = useState(null);
  const [showAiInfo, setShowAiInfo] = useState(false);
  // One-shot popup that fires the FIRST time the visitor picks the "Entspannt"
  // pace, making it crystal-clear that the 8-month level guarantee is for
  // intensive mode only. Persistent inline reminder below the selector keeps
  // them aware after dismissing.
  const [showRelaxedNotice, setShowRelaxedNotice] = useState(false);
  const [couponDescription, setCouponDescription] = useState('');
  const [couponError, setCouponError] = useState('');
  const [checkoutName, setCheckoutName] = useState('');
  const [checkoutEmail, setCheckoutEmail] = useState('');
  const [checkoutPassword, setCheckoutPassword] = useState('');
  const [showCheckoutPassword, setShowCheckoutPassword] = useState(false);
  const [checkoutLevel, setCheckoutLevel] = useState('A2');
  const [checkoutGoal, setCheckoutGoal] = useState(''); // 'travel' | 'exam'
  const [checkoutPace, setCheckoutPace] = useState(''); // 'intensive' | 'relaxed'
  const [showSubPlans, setShowSubPlans] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState('');
  const [siteSettings, setSiteSettings] = useState({ coupon_40_active: false, calendar_events: [], video_url: null });
  const nameInputRef = useRef(null);
  const emailInputRef = useRef(null);
  const passwordInputRef = useRef(null);
  const couponDebounceRef = useRef(null);

  // Load editable landing settings (video URL, calendar, 40% coupon flag)
  useEffect(() => {
    axios.get(`${API}/site/settings`)
      .then(res => setSiteSettings(res.data || {}))
      .catch(() => {});
  }, []);

  const scrollToForm = (e) => {
    e?.preventDefault();
    const el = document.getElementById('checkout-form');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setTimeout(() => nameInputRef.current?.focus(), 500);
  };

  const handleCoupon = async (rawCode) => {
    const code = (rawCode ?? couponCode).trim();
    if (!code) {
      setCouponApplied(false);
      setCouponFree(false);
      setCouponPercent(0);
      setCouponFinalAmount(null);
      setCouponDescription('');
      setCouponError('');
      return;
    }
    // Validate against backend (covers built-ins AND teacher-created custom codes)
    try {
      const res = await axios.post(`${API}/payments/validate-coupon`, { code });
      if (res.data?.valid) {
        const pct = parseInt(res.data.discount_percent, 10) || 0;
        // ── Single-coupon rule ────────────────────────────────
        // If the 40% Frühbucher-Promo is currently active site-wide and the
        // typed code does NOT offer a STRICTLY better discount, we refuse to
        // stack it. Only ONE coupon is ever valid per checkout, and the user
        // automatically gets the better of the two.
        if (siteSettings.coupon_40_active && pct <= PROMO_PERCENT) {
          setCouponApplied(false);
          setCouponFree(false);
          setCouponPercent(0);
          setCouponFinalAmount(null);
          setCouponDescription('');
          setCouponError(
            `Es ist bereits der Frühbucher-Rabatt von ${PROMO_PERCENT}% aktiv. ` +
            `Dein Code bietet nur ${pct}% — pro Bestellung ist nur EIN Gutschein gültig, ` +
            `und der größere Rabatt bleibt erhalten.`
          );
          return;
        }
        setCouponApplied(true);
        setCouponFree(pct === 100);
        setCouponPercent(pct);
        setCouponFinalAmount(res.data.final_amount ?? null);
        setCouponDescription(res.data.description || '');
        setCouponError('');
      } else {
        setCouponError('Ungültiger Gutscheincode');
        setCouponApplied(false);
        setCouponFree(false);
        setCouponPercent(0);
        setCouponFinalAmount(null);
        setCouponDescription('');
      }
    } catch {
      setCouponError('Ungültiger Gutscheincode');
      setCouponApplied(false);
      setCouponFree(false);
      setCouponPercent(0);
      setCouponFinalAmount(null);
      setCouponDescription('');
    }
  };

  /** Recurring subscription path triggered from the 3-plan grid below
   * the primary CTA. Validates the same form (name/email/password/goal/pace)
   * as the one-off checkout, then POSTs to /payments/public-subscription
   * which creates the account + Stripe customer and returns a Checkout URL
   * in mode=subscription. */
  const handleSubscribeRecurring = async (packageId) => {
    if (!checkoutName.trim() || !checkoutEmail.trim()) {
      const m = 'Bitte gib zuerst Namen und E-Mail ein';
      setCheckoutError(m); toast.error(m); return;
    }
    if (!/\S+@\S+\.\S+/.test(checkoutEmail)) {
      const m = 'Bitte gib eine gültige E-Mail ein';
      setCheckoutError(m); toast.error(m); return;
    }
    const pwdErr = validatePassword(checkoutPassword);
    if (pwdErr) { setCheckoutError(pwdErr); toast.error(pwdErr); return; }
    setCheckoutLoading(true);
    setCheckoutError('');
    try {
      const res = await axios.post(`${API}/payments/public-subscription`, {
        name: checkoutName.trim(),
        email: checkoutEmail.trim().toLowerCase(),
        password: checkoutPassword,
        package_id: packageId,
        origin_url: window.location.origin,
        spanish_level: checkoutLevel,
        learning_goal: checkoutGoal || null,
        learning_pace: checkoutPace || null,
      }, { timeout: 30000 });
      if (res.data.url) {
        toast.success('Weiterleitung zur sicheren Zahlung...');
        try {
          const price = { sub_1m: 100, sub_3m: 80, sub_6m: 60 }[packageId] || 0;
          const { trackEvent } = await import('../utils/metaPixel');
          trackEvent('InitiateCheckout', {
            content_ids: [packageId],
            content_type: 'subscription',
            content_name: { sub_1m: '1 Monat', sub_3m: '3 Monate', sub_6m: '6 Monate' }[packageId],
            currency: 'EUR',
            value: price,
            num_items: 1,
          });
          // Tiny delay so the Pixel beacon actually leaves the browser
          // before we navigate to Stripe — otherwise the request can be
          // canceled by the navigation and we lose the attribution.
          await new Promise(r => setTimeout(r, 250));
        } catch { /* pixel disabled */ }
        window.location.href = res.data.url;
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : 'Fehler beim Starten des Abos';
      setCheckoutError(msg);
      toast.error(msg);
      setCheckoutLoading(false);
    }
  };

  const handleCheckout = async () => {
    if (!checkoutName.trim() || !checkoutEmail.trim()) {
      setCheckoutError('Bitte gib deinen Namen und deine E-Mail ein');
      toast.error('Bitte fülle zuerst deinen Namen und deine E-Mail aus');
      if (!checkoutName.trim()) nameInputRef.current?.focus();
      else emailInputRef.current?.focus();
      return;
    }
    if (!/\S+@\S+\.\S+/.test(checkoutEmail)) {
      setCheckoutError('Bitte gib eine gültige E-Mail ein');
      toast.error('Bitte gib eine gültige E-Mail ein');
      emailInputRef.current?.focus();
      return;
    }
    const pwdErr2 = validatePassword(checkoutPassword);
    if (pwdErr2) {
      setCheckoutError(pwdErr2);
      toast.error(pwdErr2);
      passwordInputRef.current?.focus();
      return;
    }
    if (!checkoutGoal) {
      setCheckoutError('Bitte wähle dein Lernziel (Reisen oder Prüfung)');
      toast.error('Bitte wähle dein Lernziel');
      return;
    }
    if (!checkoutPace) {
      setCheckoutError('Bitte wähle dein Lerntempo (Intensiv oder Entspannt)');
      toast.error('Bitte wähle dein Lerntempo');
      return;
    }
    setCheckoutLoading(true);
    setCheckoutError('');
    // Coupon priority:
    //  1) User typed a code that backend validated → use it as-is (covers built-ins + custom DB codes).
    //  2) Otherwise, if 40% promo toggle is ON, auto-apply MATRIKEL40.
    //  3) Otherwise, no coupon.
    const promoActive = !!siteSettings.coupon_40_active;
    const typedCode = (couponCode || '').trim();
    const finalCouponCode = couponApplied && typedCode
      ? typedCode.toUpperCase()
      : (promoActive ? 'MATRIKEL40' : '');
    const payload = {
      name: checkoutName.trim(),
      email: checkoutEmail.trim().toLowerCase(),
      password: checkoutPassword,
      origin_url: window.location.origin,
      spanish_level: checkoutLevel,
      learning_goal: checkoutGoal,
      learning_pace: checkoutPace,
      coupon_code: finalCouponCode
    };
    // Try up to 3 times with increasing timeout — avoids false errors on cold starts
    let lastErr = null;
    for (let attempt = 1; attempt <= 3; attempt++) {
      try {
        const res = await axios.post(`${API}/payments/public-checkout`, payload, { timeout: 30000 });
        if (res.data.url) {
          toast.success(res.data.free ? 'Zugang freigeschaltet! Weiterleitung...' : 'Weiterleitung zur sicheren Zahlung...');
          // Meta Pixel — InitiateCheckout (only if free=false; free flow doesn't transact).
          if (!res.data.free) {
            try {
              const { trackEvent } = await import('../utils/metaPixel');
              trackEvent('InitiateCheckout', {
                content_ids: payload?.package_id ? [payload.package_id] : undefined,
                content_type: 'product',
              });
            } catch { /* pixel disabled */ }
          }
          window.location.href = res.data.url;
          return;
        }
      } catch (err) {
        lastErr = err;
        const status = err.response?.status;
        const detail = err.response?.data?.detail;
        // If it's a clear user-fixable error (400/409), stop retrying
        if (status && status >= 400 && status < 500 && status !== 408 && status !== 429) {
          const msg = typeof detail === 'string' ? detail : 'Fehler beim Starten der Zahlung.';
          setCheckoutError(msg);
          toast.error(msg);
          setCheckoutLoading(false);
          return;
        }
        // Network / timeout / 5xx → wait and retry
        if (attempt < 3) await new Promise(r => setTimeout(r, 800 * attempt));
      }
    }
    // Exhausted retries
    const detail = lastErr?.response?.data?.detail;
    const msg = typeof detail === 'string' ? detail : 'Verbindung langsam. Bitte noch einmal versuchen.';
    setCheckoutError(msg);
    toast.error(msg);
    setCheckoutLoading(false);
  };

  // ── Effective coupon resolution ──────────────────────────
  // Only ONE coupon can ever be active per checkout. When the teacher's 40%
  // Frühbucher promo is toggled on AND no user-typed coupon offers a strictly
  // bigger discount, we auto-apply the 40% promo so the price displayed always
  // matches what the user will actually pay.
  const promoActive = !!siteSettings.coupon_40_active;
  // User-typed code "wins" only if it's strictly better than the promo
  // (handleCoupon already rejects equal/smaller codes upfront when the promo
  // is active, so here couponApplied means it's a valid override).
  const userOverride = couponApplied;
  const effectivePercent = userOverride ? couponPercent : (promoActive ? PROMO_PERCENT : 0);
  const effectiveFinalAmount = userOverride
    ? (couponFinalAmount != null ? couponFinalAmount : 100 * (1 - couponPercent / 100))
    : (promoActive ? 100 * (1 - PROMO_PERCENT / 100) : 100);
  const effectiveSource = userOverride ? 'user' : (promoActive ? 'promo' : 'none');
  const fmtPrice = (v) => `${Number.isInteger(v) ? v : v.toFixed(2)}€`;
  const isFree = effectivePercent === 100;

  return (
    <div className="min-h-screen canvas-spain" data-testid="landing-page">
      {/* Header (with promo banner stacked on top when active) */}
      <div className="fixed top-0 left-0 right-0 z-50">
        {siteSettings.coupon_40_active && (
          <div className="bg-gradient-to-r from-[#AB1C24] to-[#EAB308] text-white py-2.5 px-4 text-center text-xs sm:text-sm font-semibold flex items-center justify-center gap-2 cursor-pointer" onClick={scrollToForm} data-testid="promo-banner">
            <Tag size={14} weight="fill" />
            <span>{siteSettings.coupon_40_label || 'Frühbucher-Rabatt'}: <span className="underline">40% Rabatt</span> bei Anmeldung — automatisch beim Bezahlen!</span>
          </div>
        )}
        <header className="bg-white/80 backdrop-blur-xl border-b border-stone-200">
          <div className="max-w-7xl mx-auto px-3 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-2">
            <div className="flex items-center gap-0 sm:gap-0 min-w-0 flex-shrink">
              <img src="/brand/hca-mark-256.png" alt="Habla" className="w-12 h-12 sm:w-20 sm:h-20 object-contain drop-shadow-sm flex-shrink-0" />
              <Wordmark size="md" tone="dark" className="-ml-1" />
            </div>
            <div className="flex items-center gap-1.5 sm:gap-3 flex-shrink-0">
              <Link to="/login">
                <Button variant="outline" size="sm" className="border-stone-300 text-[#292524] hover:border-[#AB1C24] hover:text-[#AB1C24] px-3 sm:px-4 text-sm" data-testid="login-btn">
                  Anmelden
                </Button>
              </Link>
              <a href="#checkout-form" onClick={scrollToForm} className="hidden sm:inline-block">
                <Button className="bg-[#AB1C24] hover:bg-[#8A151C] text-white" data-testid="register-btn">
                  Jetzt starten
                </Button>
              </a>
            </div>
          </div>
        </header>
      </div>

      {/* Hero Section */}
      <section className="relative pt-24 overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1570609644791-060183fd9ec7?w=1920&q=80)' }} />
        <div className="absolute inset-0 bg-gradient-to-r from-stone-900/85 via-stone-900/65 to-stone-900/40" />
        <div className="relative max-w-7xl mx-auto px-6 py-24 lg:py-36">
          <div className="max-w-2xl">
            <p className="text-[#EAB308] font-semibold tracking-[0.2em] uppercase text-sm mb-4">
              Lerne das Maximum deines Potenzials, aber wann immer du willst
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              1 Stunde <span className="text-[#EAB308] underline decoration-[#EAB308]/60 decoration-[6px] underline-offset-[6px]">pro Woche</span>. Unendlich viele Wege zu lernen.
            </h1>
            <p className="text-lg text-white/80 leading-relaxed mb-8 max-w-xl">
              <span className="bg-white/10 border border-white/20 rounded-full px-3 py-1 text-sm font-semibold mr-2 inline-block mb-2">1× Live-Kurs / Woche</span>
              Wöchentliche Online-Kurse mit Alberto, kombiniert mit KI-Übungen, Gamification und einer Lernanalyse,
              die deinen Fortschritt in Echtzeit misst und deinen Lernweg personalisiert.
            </p>
            <div className="flex flex-wrap gap-4">
              <a href="#checkout-form" onClick={scrollToForm}>
                <Button className="bg-[#AB1C24] hover:bg-[#8A151C] text-white px-8 py-6 text-lg rounded-lg shadow-lg btn-press" data-testid="hero-cta">
                  Jetzt starten <ArrowRight size={20} className="ml-2" />
                </Button>
              </a>
              <Link to="/placement-test">
                <Button variant="outline" className="border-white/30 text-white hover:bg-white/10 px-8 py-6 text-lg rounded-lg" data-testid="placement-test-btn">
                  Einstufungstest
                </Button>
              </Link>
            </div>
            <p className="text-white/70 text-sm mt-4">Einstufungstest mit Audio — in weniger als 30 Sekunden</p>
          </div>
        </div>
      </section>

      {/* ── 15-Tage Gratis-Testphase CTA ─────────────────────
          Highly visible promo block sitting right under the hero. Drives
          direct email leads to Alberto. Fires Meta Pixel "Lead" event
          (only if marketing consent is granted; otherwise the helper noops). */}
      <section className="max-w-7xl mx-auto px-6 pt-10 sm:pt-14" data-testid="free-trial-cta-section">
        <div className="relative overflow-hidden rounded-3xl border-2 border-[#EAB308]/40 bg-gradient-to-br from-[#FEF9E7] via-[#FFFBEB] to-white shadow-[0_12px_40px_rgba(234,179,8,0.18)]">
          {/* Decorative blobs */}
          <div className="absolute -top-16 -right-16 w-72 h-72 rounded-full bg-[#EAB308]/15 blur-3xl pointer-events-none" />
          <div className="absolute -bottom-20 -left-20 w-72 h-72 rounded-full bg-[#AB1C24]/10 blur-3xl pointer-events-none" />
          <div className="relative grid grid-cols-1 lg:grid-cols-[auto,1fr,auto] items-center gap-6 sm:gap-8 p-6 sm:p-10">
            {/* Big gift icon */}
            <div className="flex-shrink-0 mx-auto lg:mx-0">
              <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl bg-gradient-to-br from-[#EAB308] to-[#F59E0B] flex items-center justify-center shadow-lg shadow-[#EAB308]/30 rotate-[-6deg]">
                <Gift size={44} className="text-white" weight="fill" />
              </div>
            </div>

            {/* Headline + bullets */}
            <div className="text-center lg:text-left min-w-0">
              <div className="inline-flex items-center gap-2 bg-[#AB1C24] text-white text-[11px] font-bold tracking-[0.18em] uppercase px-3 py-1.5 rounded-full mb-3">
                <span>Neu</span>
                <span className="w-1 h-1 rounded-full bg-white/70" />
                <span>Begrenztes Angebot</span>
              </div>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#292524] leading-tight mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
                15 Tage <span className="text-[#AB1C24]">kostenlos</span> testen — ohne Risiko, ohne Karte.
              </h2>
              <p className="text-[#57534E] text-sm sm:text-base leading-relaxed mb-4 max-w-2xl">
                Schreib Alberto eine kurze E-Mail und du bekommst sofort einen <strong className="text-[#292524]">15-tägigen Vollzugang</strong> zur Akademie:
                Live-Kurse, KI-Lehrerin, Spiele, Vokabel-Album und Community. Erst danach entscheidest du, ob du dich einschreibst.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3 max-w-2xl mx-auto lg:mx-0">
                {[
                  'Voller Zugang — alle Funktionen',
                  'Keine Zahlungsdaten nötig',
                  'Jederzeit beenden',
                ].map((t) => (
                  <div key={t} className="flex items-center gap-2 text-sm text-[#292524] justify-center lg:justify-start">
                    <CheckCircle size={18} weight="fill" className="text-[#16A34A] flex-shrink-0" />
                    <span className="font-medium">{t}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* CTA button column */}
            <div className="flex flex-col items-center lg:items-end gap-2 flex-shrink-0">
              <a
                href="mailto:a.cubriamerino@gmail.com?subject=Habla%20%E2%80%94%2015%20Tage%20kostenlos%20testen&body=Hallo%20Alberto%2C%0A%0Aich%20m%C3%B6chte%20die%20Akademie%2015%20Tage%20kostenlos%20testen.%0A%0AMein%20Name%3A%20%0AMein%20Spanisch-Niveau%20(falls%20bekannt)%3A%20%0A%0AVielen%20Dank%21"
                onClick={async () => {
                  try {
                    const { trackEvent } = await import('../utils/metaPixel');
                    trackEvent('Lead', {
                      content_name: '15-Day Free Trial',
                      content_category: 'landing_cta',
                      value: 0,
                      currency: 'EUR',
                    });
                  } catch { /* pixel disabled */ }
                }}
                className="inline-flex items-center gap-2 bg-[#AB1C24] hover:bg-[#8A151C] text-white font-bold px-7 py-4 rounded-2xl shadow-xl shadow-[#AB1C24]/30 transition-all hover:scale-[1.03] btn-press text-base sm:text-lg whitespace-nowrap"
                data-testid="free-trial-cta-btn"
              >
                <PaperPlaneTilt size={20} weight="bold" />
                Jetzt 15 Tage testen
              </a>
              <p className="text-[11px] text-[#78716C] flex items-center gap-1.5 mt-1">
                <Envelope size={12} weight="duotone" />
                <span>a.cubriamerino@gmail.com</span>
              </p>
              <p className="text-[10px] text-[#A8A29E] tracking-wider uppercase">
                Antwort in 24 Stunden
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Data-Driven Learning Section — MOVED below "Alles was du brauchst" */}

      {/* How it Works */}
      <section className="bg-white border-y border-stone-200 py-20" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <p className="text-[#AB1C24] font-semibold tracking-[0.2em] uppercase text-sm mb-3">So funktioniert es</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#292524]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              1 Stunde Unterricht. Viele Wege zu üben.
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 max-w-4xl mx-auto mb-20">
            {[
              { step: '1', icon: BookOpen, title: 'Wöchentlicher Live-Kurs', desc: 'Jede Woche 1 Stunde Online-Unterricht mit Alberto, basierend auf einem strukturierten Lehrbuch.' },
              { step: '2', icon: Robot, title: 'Täglich üben mit KI', desc: 'Zwischen den Kursen übst du mit der KI-Lehrerin, Spielen, Buddy-Challenges und Hörverstehen — wann du willst.' },
              { step: '3', icon: ChartLineUp, title: 'Fortschritt messen', desc: 'Alberto analysiert deine Daten und passt den Unterricht an. Du siehst jederzeit, wie du dich verbesserst.' },
            ].map((s, i) => (
              <div key={i} className="text-center">
                <div className="w-14 h-14 rounded-2xl bg-[#AB1C24] flex items-center justify-center mx-auto mb-5 shadow-lg shadow-[#AB1C24]/20">
                  <s.icon size={28} className="text-white" weight="fill" />
                </div>
                <h3 className="text-lg font-bold text-[#292524] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>{s.title}</h3>
                <p className="text-[#57534E] leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>

          <div className="text-center mb-10">
            <p className="text-[#AB1C24] font-semibold tracking-[0.2em] uppercase text-sm mb-3">Unsere Methodik</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#292524] mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Lerne unsere Lernmethodik kennen
            </h2>
            <p className="text-[#57534E] max-w-xl mx-auto">Im Video erklärt Alberto, wie wir Spanisch unterrichten — Pädagogik, Tools und Lernpfade.</p>
          </div>
          <div className="max-w-4xl mx-auto space-y-10">
            {(() => {
              const yt = (siteSettings.video_url || '').trim();
              const ytMatch = yt.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|watch\?v=|shorts\/))([\w-]{6,})/);
              const ytId = ytMatch ? ytMatch[1] : null;
              const hasMp4 = !!siteSettings.video_mp4_size;
              if (!ytId && !hasMp4) return null;
              return (
                <div>
                  {ytId ? (
                    <div className="aspect-video w-full rounded-2xl overflow-hidden shadow-2xl shadow-stone-900/20 bg-black" data-testid="landing-yt-video">
                      <iframe
                        src={`https://www.youtube-nocookie.com/embed/${ytId}?rel=0`}
                        title="Lerne unsere Lernmethodik kennen"
                        className="w-full h-full border-0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    </div>
                  ) : (
                    <video controls preload="metadata" className="w-full aspect-video rounded-2xl shadow-2xl shadow-stone-900/20 bg-black" data-testid="landing-mp4-video">
                      <source src={`${API}/site/settings/video.mp4`} type="video/mp4" />
                    </video>
                  )}
                  <p className="text-center text-xs text-stone-500 mt-3">Video von Alberto — bleibt im Vorschau-Reel unten ergänzt.</p>
                </div>
              );
            })()}

            {/* Demo Reel — always visible (functionality preview) */}
            <div data-testid="landing-demo-reel-wrapper">
              <div className="text-center mb-4">
                <p className="text-[#AB1C24] font-semibold tracking-[0.18em] uppercase text-xs mb-1">Funktions-Vorschau</p>
                <p className="text-[#57534E] text-sm">Ein visueller Rundgang durch die Plattform — KI-Lehrerin, Spiele, Album, Community.</p>
              </div>
              <LandingDemoReel />
              <p className="text-center text-xs text-stone-500 mt-3">Bewege die Maus darüber, um das Band zu pausieren.</p>
            </div>
          </div>
        </div>
      </section>

      {/* "Persönliche Tour" — soft CTA right after the methodology preview.
          Lets hesitant visitors request a free walkthrough by email before
          committing to a paid plan. */}
      <section className="max-w-3xl mx-auto px-6 -mt-6 mb-2" data-testid="trial-contact-section">
        <div className="relative overflow-hidden rounded-2xl border border-[#D6D3D1] bg-gradient-to-br from-[#FAF9F6] via-white to-[#FEF3F2] p-6 sm:p-8 text-left shadow-sm" data-testid="trial-contact-cta">
          <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-[#AB1C24]/5 blur-3xl pointer-events-none" />
          <div className="relative flex flex-col sm:flex-row sm:items-start gap-4 sm:gap-5">
            <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-[#AB1C24]/10 flex items-center justify-center flex-shrink-0">
              <ChatCircleText size={26} weight="duotone" className="text-[#AB1C24]" />
            </div>
            <div className="flex-1 min-w-0">
              <span className="inline-block text-[10px] uppercase tracking-[0.18em] font-bold text-[#AB1C24] mb-1.5">
                Persönliche Tour
              </span>
              <h3 className="font-bold text-xl sm:text-2xl text-[#292524] leading-snug" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Noch unsicher? Schau dich vorher in Ruhe um.
              </h3>
              <p className="text-sm text-[#57534E] mt-2 leading-relaxed">
                Schreib mir kurz und du bekommst einen <strong>kostenlosen Test-Zugang</strong>.
                Alberto führt dich gern persönlich durch die Akademie, beantwortet deine Fragen
                und du entscheidest erst danach, ob du dich einschreibst.
              </p>
              <a
                href="mailto:a.cubriamerino@gmail.com?subject=Habla%20%E2%80%94%20Test-Zugang%20anfragen&body=Hallo%20Alberto%2C%0A%0Aich%20w%C3%BCrde%20gerne%20die%20Akademie%20vorab%20kennenlernen%20und%20einen%20kostenlosen%20Test-Zugang%20bekommen.%0A%0AVielen%20Dank%21"
                className="mt-5 inline-flex items-center gap-2 bg-[#AB1C24] hover:bg-[#8A151C] text-white text-sm font-semibold px-5 py-3 rounded-xl transition-colors shadow-sm hover:shadow-md"
                data-testid="trial-contact-btn"
              >
                <PaperPlaneTilt size={18} weight="bold" />
                Alberto kontaktieren
              </a>
              <p className="text-[11px] text-[#A8A29E] mt-2.5 flex items-center gap-1.5">
                <Envelope size={12} weight="duotone" />
                a.cubriamerino@gmail.com · Antwort meist innerhalb von 24 Stunden
              </p>
            </div>
          </div>
        </div>
      </section>
      {/* Value breakdown — transparenter Wertbeweis vor dem Preis-Block */}
      <section className="bg-gradient-to-b from-[#FAF9F6] to-white py-20" data-testid="value-section">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-12">
            <p className="text-[#AB1C24] font-semibold tracking-[0.2em] uppercase text-sm mb-3">Was du wirklich bekommst</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#292524] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Bewertet mit <span className="text-[#AB1C24]">100€</span> — Wert von echten Lehrern
            </h2>
            <p className="text-[#57534E] text-base sm:text-lg max-w-2xl mx-auto">
              Wir glauben an radikale Transparenz. So setzt sich der monatliche Wert deines Habla-Kurses zusammen:
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-10">
            {/* 1. Live classes */}
            <div className="bg-white rounded-2xl border-2 border-stone-200 p-6 relative overflow-hidden hover:border-[#AB1C24]/40 hover:shadow-lg transition-all">
              <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full bg-[#AB1C24]/5" />
              <div className="relative">
                <div className="w-12 h-12 rounded-xl bg-[#AB1C24]/10 flex items-center justify-center mb-4">
                  <Video size={24} weight="duotone" className="text-[#AB1C24]" />
                </div>
                <h3 className="font-bold text-[#292524] text-lg mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  4 personalisierte Live-Stunden
                </h3>
                <p className="text-sm text-[#57534E] leading-relaxed mb-4">
                  Vier 75-minütige Einzel- oder Kleingruppen-Sitzungen pro Monat — Konversation, Korrektur, Aussprache, alles in Echtzeit mit Alberto.
                </p>
                <div className="flex items-baseline gap-1.5">
                  <span className="text-3xl font-bold text-[#292524]" style={{ fontFamily: 'Outfit, sans-serif' }}>60€</span>
                  <span className="text-xs text-[#78716C]">/Monat · 12€/Std</span>
                </div>
              </div>
            </div>

            {/* 2. Platform follow-up */}
            <div className="bg-white rounded-2xl border-2 border-stone-200 p-6 relative overflow-hidden hover:border-[#2F6B50]/40 hover:shadow-lg transition-all">
              <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full bg-[#2F6B50]/5" />
              <div className="relative">
                <div className="w-12 h-12 rounded-xl bg-[#2F6B50]/10 flex items-center justify-center mb-4">
                  <ChartBar size={24} weight="duotone" className="text-[#2F6B50]" />
                </div>
                <h3 className="font-bold text-[#292524] text-lg mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Persönliche Lernbegleitung
                </h3>
                <p className="text-sm text-[#57534E] leading-relaxed mb-4">
                  Maßgeschneidertes Unterrichtsmaterial, Lernanalyse-Berichte, individuelle Anpassung des Lehrplans und <strong>24-Stunden-Support</strong> bei Fragen oder Korrekturen.
                </p>
                <div className="flex items-baseline gap-1.5">
                  <span className="text-3xl font-bold text-[#292524]" style={{ fontFamily: 'Outfit, sans-serif' }}>20€</span>
                  <span className="text-xs text-[#78716C]">/Monat</span>
                </div>
              </div>
            </div>

            {/* 3. AI platform */}
            <div className="bg-white rounded-2xl border-2 border-[#EAB308]/40 p-6 relative overflow-hidden hover:border-[#EAB308]/70 hover:shadow-lg transition-all">
              <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full bg-[#EAB308]/10" />
              <span className="absolute top-3 right-3 bg-gradient-to-r from-amber-400 to-orange-500 text-white text-[10px] font-bold tracking-wider uppercase px-2 py-0.5 rounded-full shadow-sm">Exklusiv</span>
              <div className="relative">
                <div className="w-12 h-12 rounded-xl bg-[#EAB308]/15 flex items-center justify-center mb-4">
                  <Brain size={24} weight="duotone" className="text-[#92400E]" />
                </div>
                <h3 className="font-bold text-[#292524] text-lg mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  KI-Spanischlehrer rund um die Uhr
                </h3>
                <p className="text-sm text-[#57534E] leading-relaxed mb-4">
                  Eine KI, die wie eine echte Lehrerin Fehler erklärt, Übungen generiert und mit dir spricht — unbegrenzt, jederzeit, auf deinem Niveau.
                </p>
                <div className="flex items-baseline gap-1.5 mb-2">
                  <span className="text-3xl font-bold text-[#292524]" style={{ fontFamily: 'Outfit, sans-serif' }}>21,74€</span>
                  <span className="text-xs text-[#78716C]">/Monat</span>
                </div>
                <button
                  onClick={() => setShowAiInfo(true)}
                  className="text-xs text-[#92400E] hover:text-[#78350F] font-semibold inline-flex items-center gap-1 underline underline-offset-2"
                  data-testid="ai-info-btn"
                >
                  Warum dieser Wert?
                  <CaretRight size={11} weight="bold" />
                </button>
              </div>
            </div>
          </div>

          {/* Total + discount banner */}
          <div className="bg-[#292524] text-white rounded-2xl p-8 sm:p-10 relative overflow-hidden">
            <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-[#EAB308]/10 blur-2xl" />
            <div className="absolute -bottom-12 -left-12 w-48 h-48 rounded-full bg-[#AB1C24]/15 blur-2xl" />
            <div className="relative grid grid-cols-1 md:grid-cols-[1fr,auto] gap-6 items-center">
              <div>
                <p className="text-white/60 text-xs tracking-[0.18em] uppercase mb-2 font-semibold">Gesamtwert</p>
                <div className="flex items-baseline gap-3 flex-wrap mb-3">
                  <span className="text-4xl sm:text-5xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>100€</span>
                  <span className="text-white/60 text-sm">/Monat — der ehrliche Preis</span>
                </div>
                <p className="text-white/80 text-sm leading-relaxed">
                  Bei <strong className="text-white">Einschreibung jetzt</strong> bekommst du <strong className="text-[#EAB308]">40% Rabatt</strong> auf alle Niveaus — du startest auf deinem Niveau und erreichst dein Ziel in deinem eigenen Tempo.
                </p>
              </div>
              <div className="flex-shrink-0">
                <div className="bg-gradient-to-br from-[#EAB308] to-[#F59E0B] rounded-2xl p-5 text-center shadow-xl">
                  <p className="text-[10px] font-bold tracking-[0.2em] uppercase text-[#78350F] mb-1">Aktion</p>
                  <p className="text-4xl font-bold text-[#78350F]" style={{ fontFamily: 'Outfit, sans-serif' }}>−40%</p>
                  <p className="text-[11px] text-[#78350F]/80 mt-1">auf ALLE Niveaus</p>
                </div>
              </div>
            </div>
          </div>

          {/* Guarantee */}
          <div className="mt-6 bg-gradient-to-br from-[#F3F9F5] to-white border-2 border-[#2F6B50]/30 rounded-2xl p-6 sm:p-8 relative" data-testid="guarantee-banner">
            <div className="flex items-start gap-4 flex-col sm:flex-row">
              <div className="flex-shrink-0 w-14 h-14 rounded-2xl bg-[#2F6B50] flex items-center justify-center shadow-lg">
                <SealCheck size={28} weight="fill" className="text-white" />
              </div>
              <div className="flex-1">
                <p className="text-[10px] font-bold tracking-[0.2em] uppercase text-[#2F6B50] mb-1">Unsere Garantie</p>
                <h3 className="font-bold text-[#292524] text-xl sm:text-2xl mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  In 8 Monaten ein Niveau weiter — oder dein Spanisch-Level ist <span className="text-[#2F6B50]">komplett kostenlos</span>.
                </h3>
                <p className="text-sm text-[#57534E] leading-relaxed">
                  Wir bei <strong>HablaconAlberto.de</strong> garantieren dir, dass du in 8 Monaten dein nächstes CEFR-Niveau erreichst. Falls nicht, bekommst du das gesamte Lernen dieses Niveaus geschenkt — ohne Wenn und Aber.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Pricing */}
      <section className="bg-white border-y border-stone-200 py-20" data-testid="pricing-section">
        <div className="max-w-lg mx-auto text-center px-6">
          <p className="text-[#AB1C24] font-semibold tracking-[0.2em] uppercase text-sm mb-3">Einfache Preise</p>
          <h2 className="text-3xl sm:text-4xl font-bold text-[#292524] mb-8" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Investiere in dein Spanisch
          </h2>
          <div className="bg-[#FAF9F6] rounded-2xl border-2 border-[#AB1C24] p-8 shadow-[0_8px_30px_rgba(171,28,36,0.08)]">
            <span className="inline-block bg-[#EAB308]/15 text-[#92400E] px-4 py-1.5 rounded-full text-sm font-semibold mb-6">Vollzugang</span>

            {/* Price display — ALWAYS reflects the effective price the user
                will pay (auto-promo OR user coupon, never both). */}
            {isFree ? (
              <div className="mb-2" data-testid="price-display-free">
                <div className="flex items-baseline justify-center gap-2">
                  <span className="text-2xl text-[#78716C] line-through">100€</span>
                  <span className="text-5xl font-bold text-[#16A34A]" style={{ fontFamily: 'Outfit, sans-serif' }}>0€</span>
                </div>
                <span className="inline-block mt-2 bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm font-semibold">
                  Komplett kostenlos!
                </span>
              </div>
            ) : effectivePercent > 0 ? (
              <div className="mb-2" data-testid="price-display-discounted">
                <div className="flex items-baseline justify-center gap-2 flex-wrap">
                  <span className="text-2xl text-[#78716C] line-through decoration-[#AB1C24]/60 decoration-[3px]">100€</span>
                  <span className="text-5xl font-bold text-[#AB1C24]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {fmtPrice(effectiveFinalAmount)}
                  </span>
                  <span className="text-2xl text-[#57534E]">/Monat</span>
                </div>
                <div className="flex items-center justify-center gap-2 mt-2 flex-wrap">
                  <span className="inline-block bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm font-semibold">
                    −{effectivePercent}% Rabatt aktiv
                  </span>
                  {effectiveSource === 'promo' && (
                    <span className="inline-block bg-[#EAB308]/15 text-[#92400E] px-3 py-1 rounded-full text-xs font-semibold">
                      {siteSettings.coupon_40_label || 'Frühbucher-Rabatt'}
                    </span>
                  )}
                </div>
                <p className="text-xs text-[#78716C] mt-2">
                  {effectiveSource === 'promo'
                    ? 'Der Rabatt wird beim Bezahlen automatisch angewendet.'
                    : 'Dein eingelöster Gutschein wird beim Bezahlen automatisch angewendet.'}
                </p>
              </div>
            ) : (
              <div className="flex items-baseline justify-center gap-1 mb-2" data-testid="price-display-regular">
                <span className="text-5xl font-bold text-[#292524]" style={{ fontFamily: 'Outfit, sans-serif' }}>100€</span>
                <span className="text-2xl text-[#57534E]">/Monat</span>
              </div>
            )}

            <div className="space-y-3 text-left mt-8 mb-6">
              {[
                '1 Stunde Live-Kurs pro Woche mit Alberto',
                'Unbegrenzt KI-Übungen & Sprachtraining',
                'Personalisierte Lernanalyse',
                'Community, Buddies & Challenges',
                'Gamification: Punkte, Badges & Levels',
                'Hörverstehen & Videoserie',
              ].map((feat) => (
                <div key={feat} className="flex items-center gap-3">
                  <Check size={18} className="text-[#166534] flex-shrink-0" weight="bold" />
                  <span className="text-[#292524]">{feat}</span>
                </div>
              ))}
            </div>

            {/* Coupon section — discrete, word of mouth only, auto-applies on type.
                Only ONE coupon ever active: if the 40% promo is on, a typed
                code must offer a STRICTLY bigger discount to win — otherwise
                `handleCoupon` rejects it with a German notice. */}
            {!couponApplied && (
              <div className="mb-5">
                <div className="relative">
                  <Tag size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#78716C]" />
                  <Input value={couponCode}
                    onChange={(e) => {
                      const v = e.target.value;
                      setCouponCode(v);
                      // Debounce ~400ms — only call backend after user stops typing
                      if (couponDebounceRef.current) clearTimeout(couponDebounceRef.current);
                      couponDebounceRef.current = setTimeout(() => handleCoupon(v), 400);
                    }}
                    onBlur={(e) => handleCoupon(e.target.value)}
                    placeholder={promoActive ? 'Besseren Gutscheincode? Hier eingeben' : 'Gutscheincode'}
                    className="pl-9 bg-white border-stone-300 text-sm"
                    data-testid="coupon-input" />
                </div>
                {couponError && <p className="text-red-500 text-sm mt-1.5 text-left" data-testid="coupon-error">{couponError}</p>}
                {promoActive && !couponError && (
                  <p className="text-[11px] text-[#78716C] mt-1.5 text-left">
                    Aktuell ist der {PROMO_PERCENT}%-Frühbucher-Rabatt aktiv. Pro Bestellung ist nur ein Gutschein gültig.
                  </p>
                )}
              </div>
            )}
            {couponApplied && (
              <div className="mb-5 bg-[#AB1C24]/5 border border-[#AB1C24]/20 rounded-xl p-4 text-left" data-testid="coupon-message">
                <div className="flex items-start gap-3">
                  <Heart size={22} className="text-[#AB1C24] flex-shrink-0 mt-0.5" weight="fill" />
                  <div>
                    {couponFree ? (
                      <>
                        <p className="font-semibold text-[#AB1C24] text-sm mb-1">Du bist ein Auserwählter!</p>
                        <p className="text-[#57534E] text-xs leading-relaxed">
                          Mit diesem Code erhältst du <strong className="text-[#292524]">100% Rabatt</strong> — der Zugang ist für dich komplett kostenlos. Nach dem Klick erhältst du deine Zugangsdaten per E-Mail.
                        </p>
                      </>
                    ) : (
                      <>
                        <p className="font-semibold text-[#AB1C24] text-sm mb-1">Rabatt freigeschaltet!</p>
                        {couponCode.toUpperCase() === '26954' ? (
                          <p className="text-[#57534E] text-xs leading-relaxed">
                            Nordenham hat mich mit offenen Armen empfangen und ist das Zuhause meines Babys Diego.
                            Dafür verdient ihr diesen <strong className="text-[#292524]">lebenslangen Rabatt</strong>.
                          </p>
                        ) : (
                          <p className="text-[#57534E] text-xs leading-relaxed">
                            {couponDescription || 'Dein Code wurde erfolgreich eingelöst'} — du sparst <strong className="text-[#292524]">{couponPercent}%</strong> auf das Monatsabo.
                          </p>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Checkout form */}
            <div className="space-y-3 text-left" id="checkout-form">
              <Input ref={nameInputRef} placeholder="Dein Name" value={checkoutName}
                onChange={e => { setCheckoutName(e.target.value); setCheckoutError(''); }}
                className={`py-5 rounded-xl bg-white ${checkoutError && !checkoutName.trim() ? 'border-red-500 ring-2 ring-red-200' : 'border-stone-300'}`}
                data-testid="checkout-name" />
              <Input ref={emailInputRef} placeholder="Deine E-Mail-Adresse" type="email" value={checkoutEmail}
                onChange={e => { setCheckoutEmail(e.target.value); setCheckoutError(''); }}
                className={`py-5 rounded-xl bg-white ${checkoutError && !checkoutEmail.trim() ? 'border-red-500 ring-2 ring-red-200' : 'border-stone-300'}`}
                data-testid="checkout-email" />
              <div className="relative">
                <Input ref={passwordInputRef} placeholder="Passwort (8–12 Zeichen, A-Z, 0-9, Sonderzeichen)" type={showCheckoutPassword ? 'text' : 'password'} value={checkoutPassword}
                  onChange={e => { setCheckoutPassword(e.target.value); setCheckoutError(''); }}
                  className={`py-5 pr-12 rounded-xl bg-white ${checkoutError && validatePassword(checkoutPassword) ? 'border-red-500 ring-2 ring-red-200' : 'border-stone-300'}`}
                  data-testid="checkout-password" autoComplete="new-password" />
                <button type="button" onClick={() => setShowCheckoutPassword(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#78716C] hover:text-[#AB1C24]"
                  data-testid="toggle-password-visibility" aria-label="Passwort anzeigen">
                  {showCheckoutPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                </button>
              </div>
              <PasswordRequirements password={checkoutPassword} />

              {/* Level selector */}
              <div>
                <label className="block text-sm font-medium text-[#57534E] mb-2">
                  Auf welchem Niveau möchtest du starten?
                </label>
                <div className="grid grid-cols-5 gap-1.5" data-testid="level-selector">
                  {['A1', 'A2', 'B1', 'B2', 'C1'].map(lvl => (
                    <button
                      key={lvl}
                      type="button"
                      onClick={() => setCheckoutLevel(lvl)}
                      className={`py-3 rounded-lg font-bold text-sm transition-all ${
                        checkoutLevel === lvl
                          ? 'bg-[#AB1C24] text-white shadow-lg shadow-[#AB1C24]/25 scale-105'
                          : 'bg-white border border-stone-300 text-[#57534E] hover:border-[#AB1C24]/40'
                      }`}
                      data-testid={`level-${lvl}`}
                    >
                      {lvl}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-[#A8A29E] mt-2">
                  Deine Übungen werden automatisch an dieses Niveau angepasst. Du kannst es jederzeit in deinem Profil ändern.
                </p>
                <a href="/placement-test" className="text-xs text-[#AB1C24] underline hover:text-[#8A151C] mt-1 inline-block">
                  Nicht sicher? Mach den kostenlosen Einstufungstest →
                </a>
              </div>

              {/* Goal selector */}
              <div>
                <label className="block text-sm font-medium text-[#57534E] mb-2">
                  Was ist dein Ziel?
                </label>
                <div className="grid grid-cols-2 gap-2" data-testid="goal-selector">
                  {[
                    { v: 'travel', label: 'Reisen', sub: 'Im Urlaub kommunizieren' },
                    { v: 'exam', label: 'Prüfung bestehen', sub: 'Offizielles Sprachzertifikat' },
                  ].map(g => (
                    <button
                      key={g.v}
                      type="button"
                      onClick={() => setCheckoutGoal(g.v)}
                      className={`p-3 rounded-lg text-left transition-all ${
                        checkoutGoal === g.v
                          ? 'bg-[#AB1C24] text-white shadow-lg shadow-[#AB1C24]/25 scale-[1.02]'
                          : 'bg-white border border-stone-300 text-[#57534E] hover:border-[#AB1C24]/40'
                      }`}
                      data-testid={`goal-${g.v}`}
                    >
                      <p className="font-bold text-sm">{g.label}</p>
                      <p className={`text-[11px] mt-0.5 ${checkoutGoal === g.v ? 'text-white/80' : 'text-[#A8A29E]'}`}>{g.sub}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Pace selector */}
              <div>
                <label className="block text-sm font-medium text-[#57534E] mb-2">
                  In welchem Tempo möchtest du dein Niveau erreichen?
                </label>
                <div className="grid grid-cols-2 gap-2" data-testid="pace-selector">
                  {[
                    { v: 'intensive', label: 'Intensiv', sub: 'Weniger als 6 Monate' },
                    { v: 'relaxed', label: 'Entspannt', sub: 'Etwa 1 Jahr' },
                  ].map(p => (
                    <button
                      key={p.v}
                      type="button"
                      onClick={() => {
                        setCheckoutPace(p.v);
                        // Inform user that the 8-month level guarantee does NOT apply
                        // to the relaxed pace — they explicitly chose ~1 year per level.
                        if (p.v === 'relaxed') setShowRelaxedNotice(true);
                      }}
                      className={`p-3 rounded-lg text-left transition-all ${
                        checkoutPace === p.v
                          ? 'bg-[#AB1C24] text-white shadow-lg shadow-[#AB1C24]/25 scale-[1.02]'
                          : 'bg-white border border-stone-300 text-[#57534E] hover:border-[#AB1C24]/40'
                      }`}
                      data-testid={`pace-${p.v}`}
                    >
                      <p className="font-bold text-sm">{p.label}</p>
                      <p className={`text-[11px] mt-0.5 ${checkoutPace === p.v ? 'text-white/80' : 'text-[#A8A29E]'}`}>{p.sub}</p>
                    </button>
                  ))}
                </div>
                {/* Permanent inline reminder while "Entspannt" is selected so the
                    user never forgets the guarantee exclusion they accepted. */}
                {checkoutPace === 'relaxed' && (
                  <div className="mt-2 bg-amber-50 border border-amber-200 rounded-lg p-2.5 flex items-start gap-2" data-testid="relaxed-inline-notice">
                    <Lightbulb size={14} className="text-amber-600 flex-shrink-0 mt-0.5" weight="duotone" />
                    <p className="text-[11px] text-amber-900 leading-snug">
                      Hinweis: Die <strong>8-Monats-Niveau-Garantie</strong> gilt nicht für „Entspannt" (≈ 1 Jahr pro Niveau).
                    </p>
                  </div>
                )}
              </div>

              {checkoutError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2" data-testid="checkout-error">
                  <X size={18} className="text-red-500 flex-shrink-0 mt-0.5" weight="bold" />
                  <p className="text-red-700 text-sm font-medium">{checkoutError}</p>
                </div>
              )}
            </div>

            <Button onClick={handleCheckout} disabled={checkoutLoading}
              className="w-full mt-4 bg-[#AB1C24] hover:bg-[#8A151C] text-white py-6 text-lg rounded-xl btn-press" data-testid="pricing-cta-button">
              {checkoutLoading
                ? 'Wird bearbeitet...'
                : isFree
                  ? 'Kostenlos freischalten'
                  : effectivePercent > 0
                    ? `Jetzt für ${fmtPrice(effectiveFinalAmount)}/Monat starten`
                    : 'Jetzt für 100€/Monat starten'}
            </Button>

            {/* Recurring subscription option — same UX as PlacementTestPage.
                Collapsible to keep the primary CTA above the fold. Hidden
                while a free / discounted coupon is in play (those still go
                through the one-off /public-checkout flow). */}
            {!isFree && (
              <div className="mt-3" data-testid="landing-subscription-wrap">
                <button
                  type="button"
                  onClick={() => {
                    const next = !showSubPlans;
                    setShowSubPlans(next);
                    // Fire ViewContent only on expand. Use window.fbq directly
                    // (set by MetaPixelManager after consent) — no need to
                    // chunk-load metaPixel.js for a single sync call.
                    if (next && typeof window !== 'undefined' && window.fbq) {
                      try {
                        window.fbq('track', 'ViewContent', {
                          content_type: 'subscription_plans',
                          content_name: 'Landing — Dauerabo Toggle',
                          content_category: 'subscription',
                        });
                      } catch { /* ignore */ }
                    }
                  }}
                  className="w-full inline-flex items-center justify-center gap-2 py-3 text-sm font-semibold text-[#292524] bg-white border-2 border-[#D6D3D1] hover:border-[#AB1C24] hover:text-[#AB1C24] rounded-xl transition-colors"
                  data-testid="landing-open-subscription-btn"
                >
                  <Crown size={18} weight="fill" className="text-amber-500" />
                  Lieber ein Dauerabo abschließen?
                  <CaretDown size={14} className={`transition-transform ${showSubPlans ? 'rotate-180' : ''}`} />
                </button>
                {showSubPlans && (
                  <>
                    <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2" data-testid="landing-subscription-plans-grid">
                      {[
                        { id: 'sub_1m', label: '1 Monat',  perMonth: 100, savings: null,             highlight: false },
                        { id: 'sub_3m', label: '3 Monate', perMonth: 80,  savings: '20% günstiger',  highlight: true  },
                        { id: 'sub_6m', label: '6 Monate', perMonth: 60,  savings: '40% günstiger',  highlight: false },
                      ].map(p => (
                        <button
                          key={p.id}
                          onClick={() => handleSubscribeRecurring(p.id)}
                          disabled={checkoutLoading}
                          className={`relative text-left p-3 rounded-xl border-2 transition-all disabled:opacity-50 ${
                            p.highlight
                              ? 'border-[#AB1C24] bg-gradient-to-br from-red-50 to-white hover:shadow-md'
                              : 'border-stone-200 bg-[#FAF9F6] hover:border-[#AB1C24]'
                          }`}
                          data-testid={`landing-sub-${p.id}-btn`}
                        >
                          {p.highlight && (
                            <span className="absolute -top-2 right-2 text-[9px] font-bold bg-[#AB1C24] text-white px-1.5 py-0.5 rounded-full uppercase tracking-wider">
                              Beliebt
                            </span>
                          )}
                          <p className="font-heading font-bold text-[#292524] text-sm">{p.label}</p>
                          <p className="mt-0.5 text-xl font-bold text-[#AB1C24]">
                            {p.perMonth}€<span className="text-xs text-[#78716C] font-normal">/Monat</span>
                          </p>
                          {p.savings && (
                            <p className="text-[10px] text-emerald-700 font-semibold">{p.savings}</p>
                          )}
                        </button>
                      ))}
                    </div>
                    <p className="text-[11px] text-[#A8A29E] mt-2 text-center px-2">
                      Automatische monatliche Verlängerung · jederzeit aus dem Profil kündbar · sichere Zahlung über Stripe
                    </p>
                    {couponApplied && !isFree && (
                      <div className="mt-2 bg-amber-50 border border-amber-300 rounded-lg p-2.5 flex items-start gap-2" data-testid="landing-coupon-sub-warning">
                        <span className="text-amber-600 text-base leading-none">⚠️</span>
                        <p className="text-[11px] text-amber-800 leading-snug">
                          <strong>Hinweis:</strong> Dein Rabatt-Code gilt nur für die Einmalzahlung oben.
                          Abonnements behalten den vollen Preis.
                        </p>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
            <p className="text-xs text-[#A8A29E] mt-3 text-center">
              {isFree
                ? 'Du erhältst deine Zugangsdaten sofort per E-Mail. Keine Zahlungsdaten erforderlich.'
                : 'Nach der Zahlung erhältst du deine Zugangsdaten per E-Mail. Sichere Zahlung via Stripe.'}
            </p>
          </div>
        </div>
      </section>

      {/* Features Bento Grid */}
      <section className="max-w-7xl mx-auto px-6 py-20" data-testid="features-section">
        <div className="text-center mb-16">
          <p className="text-[#AB1C24] font-semibold tracking-[0.2em] uppercase text-sm mb-3">Alles was du brauchst</p>
          <h2 className="text-3xl sm:text-4xl font-bold text-[#292524]" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Deine Lernwerkzeuge
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 stagger-children">
          {[
            { icon: GraduationCap, title: 'Tägliche Übungen', desc: 'Grammatik, Vokabeln und Hörverstehen — täglich neue Inhalte auf deinem Niveau.', color: '#AB1C24' },
            { icon: Robot, title: 'KI-Lehrerin', desc: 'Sprich per Mikrofon mit deiner virtuellen Lehrerin. Korrektur und Feedback in Echtzeit.', color: '#8A151C' },
            { icon: Users, title: 'Buddy-System', desc: 'Finde Lernpartner und übt gemeinsam. Challenges und Austausch in der Community.', color: '#166534' },
            { icon: Video, title: 'Live-Kurse', desc: '1 Stunde pro Woche Online-Unterricht mit Alberto. Kein Download nötig — direkt im Browser.', color: '#1E3A8A' },
            { icon: GameController, title: 'Gamification', desc: 'Punkte, Badges, Levels und Bestenlisten. Lernen, das motiviert — spielerisch zum Erfolg.', color: '#EAB308', link: 'https://de.wikipedia.org/wiki/Gamification', linkText: 'Was ist Gamification?' },
            { icon: Microphone, title: 'Sprach-Training', desc: 'Aufnahmen, Hörverstehen und mündliche Übungen. Trainiere deine Aussprache.', color: '#AB1C24' },
          ].map((f, i) => (
            <div key={i} className="bg-white rounded-xl border border-stone-200 p-8 card-hover">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-5" style={{ backgroundColor: `${f.color}12` }}>
                <f.icon size={26} style={{ color: f.color }} weight="duotone" />
              </div>
              <h3 className="text-xl font-bold text-[#292524] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>{f.title}</h3>
              <p className="text-[#57534E] leading-relaxed">{f.desc}</p>
              {f.link && (
                <a href={f.link} target="_blank" rel="noopener noreferrer" className="inline-block mt-3 text-sm text-[#AB1C24] font-medium hover:underline">
                  {f.linkText} &rarr;
                </a>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Data-Driven Learning Section — moved here per request */}
      <section className="max-w-7xl mx-auto px-6 py-20" data-testid="analytics-section">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-[#AB1C24] font-semibold tracking-[0.2em] uppercase text-sm mb-3">Lernanalyse in Echtzeit</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#292524] mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Dein Fortschritt, sichtbar gemacht
            </h2>
            <p className="text-[#57534E] leading-relaxed mb-6">
              Alberto ist nicht nur Spanischlehrer — er ist auch{' '}
              <a href="https://de.wikipedia.org/wiki/Data_Science" target="_blank" rel="noopener noreferrer" className="text-[#AB1C24] font-semibold underline decoration-[#AB1C24]/30 hover:decoration-[#AB1C24]">
                Data Scientist
              </a>. Dein Lernverhalten wird kontinuierlich analysiert: Welche Grammatikthemen sitzen, wo du Lücken hast, wie dein Hörverstehen sich entwickelt. Das Ergebnis: ein Lernweg, der sich an dich anpasst.
            </p>
            <div className="space-y-4">
              {[
                { icon: ChartLineUp, text: 'Detaillierte Fortschrittsberichte nach jedem Monat' },
                { icon: Brain, text: 'KI-gestützte Übungen, die deine Schwächen gezielt trainieren' },
                { icon: TrendUp, text: 'Echtzeit-Tracking von Vokabeln, Grammatik und Hörverstehen' },
                { icon: Lightbulb, text: 'Personalisierte Empfehlungen basierend auf deinen Daten' },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-[#AB1C24]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <item.icon size={18} className="text-[#AB1C24]" weight="duotone" />
                  </div>
                  <p className="text-[#292524]">{item.text}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="bg-white rounded-2xl border border-stone-200 p-6 shadow-lg">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-semibold text-[#292524]">Lernfortschritt — Beispiel</h3>
              <span className="text-xs bg-green-100 text-green-700 px-2.5 py-1 rounded-full font-medium">+2 Niveaus in 6 Monaten</span>
            </div>
            <ProgressChart />
            <p className="text-sm text-[#78716C] mt-3 text-center">
              Von A2 auf B1 — gemessen durch Übungen, Tests und Sprachanalyse
            </p>
          </div>
        </div>
      </section>

      {/* Enrollment calendar — editable from teacher panel */}
      {(siteSettings.calendar_events && siteSettings.calendar_events.length > 0) && (
        <section className="max-w-7xl mx-auto px-6 py-14" data-testid="enrollment-calendar">
          <div className="text-center mb-8">
            <p className="text-[#AB1C24] font-semibold tracking-[0.2em] uppercase text-sm mb-3">Nächste Anmeldungen</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#292524] mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Wähle deinen Starttermin
            </h2>
            {siteSettings.coupon_40_active && (
              <p className="text-[#57534E] max-w-2xl mx-auto">
                Melde dich jetzt an und erhalte automatisch <span className="font-bold text-[#AB1C24]">40% Rabatt</span> beim Bezahlen.
              </p>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl mx-auto">
            {siteSettings.calendar_events.map(ev => (
              <div key={ev.id} className="bg-white rounded-2xl border border-stone-200 p-5 shadow-sm hover:shadow-md transition-shadow" data-testid={`cal-${ev.id}`}>
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-lg bg-[#AB1C24]/10 flex items-center justify-center">
                    <Clock size={20} className="text-[#AB1C24]" weight="duotone" />
                  </div>
                  <p className="font-bold text-[#292524]">{ev.date}</p>
                </div>
                <p className="font-semibold text-[#292524] mb-1">{ev.title}</p>
                {ev.note && <p className="text-sm text-[#78716C]">{ev.note}</p>}
              </div>
            ))}
          </div>
          <div className="text-center mt-8">
            <a href="#checkout-form" onClick={scrollToForm}>
              <Button className="bg-[#AB1C24] hover:bg-[#8A151C] text-white px-8 py-5 rounded-lg" data-testid="calendar-cta">
                {siteSettings.coupon_40_active ? 'Jetzt mit 40% Rabatt anmelden' : 'Jetzt anmelden'}
                <ArrowRight size={18} className="ml-2" />
              </Button>
            </a>
          </div>
        </section>
      )}


      {/* "Entspannt" → guarantee-exclusion popup. Fired the first time the
          visitor picks the relaxed pace inside the checkout. After dismissing,
          a persistent inline note remains under the selector. */}
      {showRelaxedNotice && (
        <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4" onClick={() => setShowRelaxedNotice(false)} data-testid="relaxed-pace-modal">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="bg-gradient-to-br from-amber-400 to-orange-500 p-6 text-white relative">
              <button onClick={() => setShowRelaxedNotice(false)} className="absolute top-3 right-3 p-1.5 rounded-lg hover:bg-white/20 text-white" aria-label="Schließen">
                <X size={18} weight="bold" />
              </button>
              <div className="w-12 h-12 rounded-xl bg-white/25 flex items-center justify-center mb-3 backdrop-blur-sm">
                <Clock size={26} weight="duotone" />
              </div>
              <h3 className="font-bold text-xl mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Entspanntes Tempo gewählt
              </h3>
              <p className="text-white/90 text-sm">
                Etwa ein Jahr pro CEFR-Niveau — perfekt für nebenberufliches Lernen.
              </p>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <p className="text-xs font-bold tracking-wider uppercase text-amber-800 mb-2 flex items-center gap-1.5">
                  <Lightbulb size={14} weight="fill" />
                  Wichtiger Hinweis
                </p>
                <p className="text-sm text-[#292524] leading-relaxed">
                  Unsere <strong className="text-[#2F6B50]">8-Monats-Niveau-Garantie</strong> gilt ausschließlich für das Tempo <strong>„Intensiv"</strong> (weniger als 6 Monate Lerneinsatz). Im entspannten Modus planst du selbst ca. 12 Monate für dein nächstes Niveau ein — die Garantie ist deshalb nicht anwendbar.
                </p>
              </div>
              <p className="text-sm text-[#57534E] leading-relaxed">
                Alle anderen Vorteile bleiben dir erhalten: Live-Kurse, KI-Lehrer, Lernanalyse, Materialien und der <strong className="text-[#AB1C24]">40% Frühbucher-Rabatt</strong>.
              </p>
              <div className="flex items-center gap-2 pt-2">
                <Button
                  onClick={() => { setCheckoutPace('intensive'); setShowRelaxedNotice(false); }}
                  variant="outline"
                  className="flex-1 border-stone-300"
                  data-testid="switch-to-intensive"
                >
                  Doch Intensiv wählen
                </Button>
                <Button
                  onClick={() => setShowRelaxedNotice(false)}
                  className="flex-1 bg-[#AB1C24] hover:bg-[#8A151C] text-white"
                  data-testid="accept-relaxed"
                >
                  Verstanden
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* AI-info modal — explains the "21,74€/mes" value */}
      {showAiInfo && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setShowAiInfo(false)} data-testid="ai-info-modal">
          <div className="bg-white rounded-2xl shadow-2xl max-w-xl w-full max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="p-6 border-b border-stone-200 flex items-start justify-between gap-3">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#EAB308]/15 flex items-center justify-center flex-shrink-0">
                  <Brain size={22} weight="duotone" className="text-[#92400E]" />
                </div>
                <div>
                  <h3 className="font-bold text-[#292524] text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Warum ein KI-Lehrer 21,74€/Monat wert ist
                  </h3>
                  <p className="text-xs text-[#78716C] mt-0.5">Transparenter Hintergrund</p>
                </div>
              </div>
              <button onClick={() => setShowAiInfo(false)} className="p-1.5 rounded-lg hover:bg-stone-100 flex-shrink-0">
                <X size={18} />
              </button>
            </div>
            <div className="p-6 space-y-4 text-sm text-[#292524] leading-relaxed">
              <p>
                Heute kennen wir <strong>keine kommerzielle Plattform</strong>, die das leistet, was unsere KI tut: Sie versteht den Kontext deines Niveaus, erklärt Grammatik-Fehler wie eine echte Lehrerin, generiert maßgeschneiderte Übungen und führt natürliche Gespräche auf Spanisch — alles 24/7.
              </p>
              <p>
                Es <strong>ist</strong> möglich, eine kostenlose Version anzubieten — genau das tun wir mit Habla. Wer programmieren kann und das Wissen hat, dem entstehen keine teuren Lizenzgebühren, sondern nur die Cloud-Kosten der Sprachmodelle.
              </p>
              <p className="bg-[#FFFBEB] border border-[#EAB308]/40 rounded-xl p-4">
                <strong className="text-[#92400E]">Unser Versprechen:</strong> Du zahlst <span className="line-through text-[#78716C]">21,74€</span> <strong className="text-[#16A34A]">0€</strong> für diese KI — sie ist im Habla-Abo komplett enthalten. Wir berechnen sie hier nur, damit du den realen Marktwert siehst.
              </p>
              <p className="text-xs text-[#78716C] pt-2 border-t border-stone-200">
                Vergleichbare KI-Tutoren auf dem Markt (Speak, ELSA, Lingoda+) liegen zwischen 14€ und 30€/Monat. Unser Wert wurde konservativ am unteren Drittel angesetzt.
              </p>
            </div>
            <div className="p-4 border-t border-stone-200 flex justify-end">
              <Button onClick={() => setShowAiInfo(false)} className="bg-[#AB1C24] hover:bg-[#8A151C] text-white">
                Verstanden
              </Button>
            </div>
          </div>
        </div>
      )}


      {/* Founder Section */}
      <section className="bg-[#292524] py-20" data-testid="founder-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16">
            <div className="flex-shrink-0">
              <div className="w-64 h-64 lg:w-80 lg:h-80 rounded-2xl overflow-hidden border-4 border-white/10 shadow-2xl">
                <img src={ALBERTO_PHOTO} alt="Alberto Cubria" className="w-full h-full object-cover" data-testid="founder-photo" />
              </div>
            </div>
            <div className="text-center lg:text-left">
              <p className="text-[#EAB308] font-semibold tracking-[0.2em] uppercase text-sm mb-3">Lehrer, Data Scientist & Gründer</p>
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Alberto Cubria
              </h2>
              <p className="text-white/85 text-lg leading-relaxed mb-4">
                Mehr als <strong className="text-white">10 Jahre Erfahrung</strong> in der Bildung, davon 4 Jahre als Spanischlehrer
                für deutschsprachige Erwachsene — sowohl an der{' '}
                <span className="text-[#EAB308]">VHS</span> als auch im Privatunterricht.
              </p>
              <p className="text-white/70 leading-relaxed mb-4">
                7 Jahre Erfahrung in der Gründung und Leitung von <strong className="text-white/90">Bildungszentren mit Fokus auf neue Technologien</strong>. Alberto verbindet
                seine Lehrkarriere als öffentlicher Lehrer in Spanien mit Unternehmertum: Seine Zentren zeichneten sich durch
                den Einsatz von <strong className="text-white/90">Datenanalyse</strong> zur Optimierung des Lernprozesses,{' '}
                <a href="https://de.wikipedia.org/wiki/Gamification" target="_blank" rel="noopener noreferrer" className="text-[#EAB308] underline decoration-[#EAB308]/40 hover:decoration-[#EAB308]">
                  Gamification
                </a>{' '}und <strong className="text-white/90">KI-gestütztem Unterricht</strong> aus.
              </p>
              <p className="text-white/70 leading-relaxed mb-6">
                Gründer der angesehenen Sprach- und Fluglotsenakademie <strong className="text-white/90">AulaHome</strong>.
                Mit Habla bringt er all diese Erfahrung in eine Plattform, die Unterricht, Technologie und Datenanalyse vereint.
              </p>
              <div className="flex items-center gap-4 flex-wrap">
                <div className="flex items-center gap-2 text-[#EAB308]">
                  <Globe size={18} />
                  <span className="font-medium text-sm">Nordenham, Deutschland</span>
                </div>
                <div className="flex items-center gap-2 text-[#EAB308]">
                  <Clock size={18} />
                  <span className="font-medium text-sm">+10 Jahre Lehrerfahrung</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Qualifications Section */}
      <section className="max-w-7xl mx-auto px-6 py-20" data-testid="qualifications-section">
        <div className="text-center mb-12">
          <p className="text-[#AB1C24] font-semibold tracking-[0.2em] uppercase text-sm mb-3">Qualifikationen</p>
          <h2 className="text-3xl sm:text-4xl font-bold text-[#292524]" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Albertos akademische Titel
          </h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl mx-auto mb-8">
          {TITULOS.map((titulo, i) => (
            <button
              key={i}
              onClick={() => setViewingTitulo(titulo)}
              className="bg-white rounded-xl border border-stone-200 p-5 text-left hover:shadow-md hover:border-[#AB1C24]/30 transition-all group"
              data-testid={`titulo-${i}`}
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#AB1C24]/10 flex items-center justify-center flex-shrink-0">
                  <Certificate size={22} className="text-[#AB1C24]" weight="duotone" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-[#292524] text-sm leading-snug group-hover:text-[#AB1C24] transition-colors">{titulo.name}</p>
                  <p className="text-xs text-[#78716C] mt-1 flex items-center gap-1">
                    <Eye size={12} /> Titel ansehen
                  </p>
                </div>
              </div>
            </button>
          ))}
          {/* Extra qualifications without PDF */}
          <div className="bg-[#FAF9F6] rounded-xl border border-stone-200 p-5">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#EAB308]/10 flex items-center justify-center flex-shrink-0">
                <GraduationCap size={22} className="text-[#EAB308]" weight="duotone" />
              </div>
              <div>
                <p className="font-semibold text-[#292524] text-sm leading-snug">Weitere Qualifikationen</p>
                <p className="text-xs text-[#78716C] mt-1">Pedagogía Terapéutica, profesor de educación cristiana, +1.200 Stunden Fortbildungen</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Levels Section */}
      <section className="bg-white border-y border-stone-200 py-16" data-testid="levels-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-10">
            <p className="text-[#AB1C24] font-semibold tracking-[0.2em] uppercase text-sm mb-3">Für jedes Niveau</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#292524]" style={{ fontFamily: 'Outfit, sans-serif' }}>Von A1 bis C1</h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 max-w-3xl mx-auto">
            {['A1', 'A2', 'B1', 'B2', 'C1'].map((level) => (
              <div key={level} className="text-center p-6 rounded-xl border border-stone-200 bg-[#FAF9F6] card-hover">
                <span className={`level-badge ${level} text-lg px-4 py-1.5 mx-auto`}>{level}</span>
                <p className="text-sm text-[#57534E] mt-3">
                  {level === 'A1' ? 'Anfänger' : level === 'A2' ? 'Grundlagen' : level === 'B1' ? 'Mittelstufe' : level === 'B2' ? 'Fortgeschritten' : 'Experte'}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <InstallAppCard />

      {/* Footer */}
      <footer className="bg-[#1C1917] py-12" data-testid="footer">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-6 pb-10 border-b border-stone-700">
            <div className="flex items-center gap-3">
              <img src="/brand/hca-mark-128.png" alt="Habla" className="w-14 h-14 object-contain" />
              <Wordmark size="md" tone="light" />
            </div>
            <button
              onClick={() => setShowLegal(true)}
              className="text-stone-400 hover:text-white text-sm underline underline-offset-4"
              data-testid="open-legal-btn"
            >
              AGB & Datenschutz
            </button>
            <p className="text-stone-500 text-sm text-center sm:text-right">
              2026 Habla · <a href="mailto:a.cubriamerino@gmail.com" className="underline hover:text-white">a.cubriamerino@gmail.com</a>
            </p>
          </div>
          <p className="text-stone-500 text-xs text-center mt-6">
            Erstellt von Alberto Cubria in Nordenham, Deutschland · Zahlungen sicher abgewickelt via Stripe
          </p>
        </div>
      </footer>

      {/* Legal Modal */}
      {showLegal && (
        <div
          className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setShowLegal(false)}
          data-testid="legal-modal"
        >
          <div
            className="bg-white rounded-2xl max-w-3xl w-full max-h-[85vh] overflow-y-auto shadow-2xl"
            onClick={e => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white border-b border-stone-200 px-6 py-4 flex items-center justify-between z-10">
              <h2 className="font-bold text-xl text-[#292524]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Allgemeine Geschäftsbedingungen & Datenschutz
              </h2>
              <button
                onClick={() => setShowLegal(false)}
                className="p-2 rounded-lg hover:bg-stone-100 text-stone-500"
                data-testid="close-legal-btn"
                aria-label="Schließen"
              >
                <X size={22} />
              </button>
            </div>
            <div className="px-6 py-6 space-y-6 text-[#292524] text-sm leading-relaxed">
              <section>
                <h3 className="font-bold text-lg mb-2">1. Anbieter</h3>
                <p>
                  Habla — Online-Sprachakademie für Spanisch.<br />
                  Inhaber: Alberto Cubria<br />
                  Sitz: Nordenham, Deutschland<br />
                  Kontakt: <a href="mailto:a.cubriamerino@gmail.com" className="text-[#AB1C24] underline">a.cubriamerino@gmail.com</a>
                </p>
              </section>

              <section>
                <h3 className="font-bold text-lg mb-2">2. Vertragsgegenstand & Preise</h3>
                <p>
                  Wir bieten Zugang zu einer Online-Spanischlernplattform mit Live-Unterricht, KI-gestützten Übungen,
                  interaktiver Community und Lernmaterialien. Die Nutzung erfolgt im Rahmen eines Monatsabonnements zum
                  Preis von <strong>100 € pro Monat</strong> (inkl. ggf. gesetzlicher MwSt.). Die Zahlung erfolgt im Voraus
                  und verlängert sich automatisch, bis sie vom Nutzer gekündigt wird.
                </p>
              </section>

              <section>
                <h3 className="font-bold text-lg mb-2">3. Rücktrittsrecht & Rückerstattungen</h3>
                <p className="mb-2">
                  Verbraucher haben gemäß § 355 BGB grundsätzlich ein Widerrufsrecht von <strong>14 Tagen</strong> ab
                  Vertragsschluss. Das Widerrufsrecht erlischt jedoch bei digitalen Inhalten, sobald mit der Ausführung
                  der Dienstleistung begonnen wurde, nachdem der Nutzer ausdrücklich zugestimmt hat (§ 356 Abs. 5 BGB).
                </p>
                <p className="mb-2">
                  <strong>Die Plattform behält sich das Recht vor, Rückerstattungen gemäß den gesetzlichen Bestimmungen
                  zu gewähren oder abzulehnen.</strong> Eine Rückerstattung erfolgt nicht, wenn der Nutzer die Inhalte
                  bereits in nennenswertem Umfang genutzt hat. Anträge auf Rückerstattung sind per E-Mail an{' '}
                  <a href="mailto:a.cubriamerino@gmail.com" className="text-[#AB1C24] underline">a.cubriamerino@gmail.com</a>{' '}
                  zu richten und werden innerhalb von 14 Werktagen bearbeitet.
                </p>
              </section>

              <section>
                <h3 className="font-bold text-lg mb-2">4. Kündigung</h3>
                <p>
                  Das Abonnement kann jederzeit zum Ende des aktuellen Abrechnungszeitraums gekündigt werden. Die
                  Kündigung erfolgt über das Nutzerkonto oder per E-Mail an den Anbieter. Bereits gezahlte Beträge für
                  den laufenden Monat werden nicht erstattet.
                </p>
              </section>

              <section>
                <h3 className="font-bold text-lg mb-2">5. Datenschutz (DSGVO / LGPD / GDPR)</h3>
                <p className="mb-2">
                  Der Schutz Ihrer personenbezogenen Daten ist uns wichtig. Die Verarbeitung erfolgt nach Maßgabe der
                  europäischen Datenschutz-Grundverordnung (DSGVO / GDPR) sowie — sofern anwendbar — des brasilianischen
                  Lei Geral de Proteção de Dados (LGPD).
                </p>
                <p className="mb-2"><strong>Erhobene Daten:</strong> Name, E-Mail-Adresse, Zahlungsdaten (ausschließlich
                  über Stripe verarbeitet), Lernfortschritt, Interaktionen mit der KI-Lehrerin und Forumsbeiträge.</p>
                <p className="mb-2"><strong>Zweck der Verarbeitung:</strong> Bereitstellung des Dienstes, personalisierte
                  Lernanalyse, Kommunikation mit Nutzern, Abrechnung.</p>
                <p className="mb-2"><strong>Rechtsgrundlage:</strong> Art. 6 Abs. 1 lit. b DSGVO (Vertragserfüllung),
                  lit. a (Einwilligung bei Marketing-Mails), lit. f (berechtigtes Interesse bei Sicherheitslogs).</p>
                <p className="mb-2"><strong>Ihre Rechte:</strong> Auskunft (Art. 15 DSGVO), Berichtigung (Art. 16),
                  Löschung / „Recht auf Vergessenwerden" (Art. 17), Einschränkung der Verarbeitung (Art. 18),
                  Datenübertragbarkeit (Art. 20), Widerspruch (Art. 21), Beschwerderecht bei einer Aufsichtsbehörde
                  (Art. 77). Anfragen richten Sie an{' '}
                  <a href="mailto:a.cubriamerino@gmail.com" className="text-[#AB1C24] underline">a.cubriamerino@gmail.com</a>.</p>
                <p className="mb-2"><strong>Datenweitergabe:</strong> Zahlungsabwicklung via Stripe (USA, EU-Standardvertragsklauseln),
                  KI-Funktionen via Anthropic / OpenAI (EU/USA, DPA abgeschlossen), E-Mail-Versand via Resend.
                  Keine Weitergabe an Dritte zu Werbezwecken.</p>
                <p className="mb-2"><strong>Marketing &amp; Tracking (nur mit Einwilligung):</strong> Wir setzen den
                  Meta Pixel (Facebook/Instagram, Meta Platforms Ireland Ltd.) zur Messung von Werbekampagnen ein.
                  Verarbeitete Daten: gehashte E-Mail, IP-Adresse, Browser-/Geräteinformationen, besuchte Seiten,
                  Anmeldungen und Käufe. Rechtsgrundlage: Art. 6 Abs. 1 lit. a DSGVO (Einwilligung über das
                  Cookie-Banner). Datenübertragung in die USA auf Basis der EU-Standardvertragsklauseln. Sie können
                  Ihre Einwilligung jederzeit widerrufen — entfernen Sie hierfür den Eintrag <code>hca_cookie_consent_v2</code>
                  aus Ihrem Browser-Speicher oder kontaktieren Sie uns. Wenn Sie keine Einwilligung erteilen, wird der
                  Pixel-Code nicht in die Seite eingebunden.</p>
                <p><strong>Speicherdauer:</strong> Nutzerdaten werden für die Dauer des Vertrags gespeichert und danach
                  gemäß gesetzlicher Aufbewahrungsfristen (z. B. 10 Jahre für Rechnungen nach § 147 AO) archiviert.
                  Lerndaten können jederzeit auf Anfrage gelöscht werden.</p>
              </section>

              <section>
                <h3 className="font-bold text-lg mb-2">6. Haftung</h3>
                <p>
                  Der Anbieter haftet nicht für mittelbare Schäden, entgangenen Gewinn oder unterbrochene Nutzung aufgrund
                  technischer Störungen. Die Inhalte dienen ausschließlich Lernzwecken und ersetzen keine
                  offizielle Sprachprüfung. Für die Richtigkeit von KI-generierten Inhalten wird keine Gewähr übernommen.
                </p>
              </section>

              <section>
                <h3 className="font-bold text-lg mb-2">7. Geistiges Eigentum</h3>
                <p>
                  Alle Lernmaterialien, Videos, Übungen und Software sind urheberrechtlich geschützt. Eine
                  Weiterverbreitung, Vervielfältigung oder kommerzielle Nutzung ohne schriftliche Genehmigung ist untersagt.
                </p>
              </section>

              <section>
                <h3 className="font-bold text-lg mb-2">8. Anwendbares Recht & Gerichtsstand</h3>
                <p>
                  Es gilt deutsches Recht. Gerichtsstand für Kaufleute ist Nordenham, Deutschland. Für Verbraucher gelten
                  die gesetzlichen Bestimmungen. Die EU-Kommission stellt eine Plattform zur Online-Streitbeilegung (OS)
                  bereit: <a href="https://ec.europa.eu/consumers/odr" target="_blank" rel="noopener noreferrer" className="text-[#AB1C24] underline">ec.europa.eu/consumers/odr</a>.
                </p>
              </section>

              <section>
                <h3 className="font-bold text-lg mb-2">9. Kontakt</h3>
                <p>
                  Für alle Fragen, Beschwerden, Rückerstattungsanträge oder Datenschutzanfragen erreichen Sie uns unter:<br />
                  <a href="mailto:a.cubriamerino@gmail.com" className="text-[#AB1C24] underline font-semibold text-base">
                    a.cubriamerino@gmail.com
                  </a>
                </p>
              </section>

              <p className="text-xs text-stone-400 pt-4 border-t border-stone-100">
                Stand: April 2026 · Diese Bedingungen können gelegentlich aktualisiert werden. Wesentliche Änderungen
                werden per E-Mail kommuniziert.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* PDF Viewer Modal */}
      {viewingTitulo && <PdfViewerModal titulo={viewingTitulo} onClose={() => setViewingTitulo(null)} />}
    </div>
  );
};

export default LandingPage;
